"use client"

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { useAuthToken } from "@/hooks/use-auth"

// API client configuration - empty string means same origin (Vite proxies /api to Express)
const API_BASE = ""

// ============================================
// Type Definitions
// ============================================

export interface User {
  id: string
  email: string
  name: string | null
  avatarUrl: string | null
  status: "active" | "invited"
  lastActive: string | null
  createdAt: string
  roles: Array<{
    roleId: string
    roleName: string
    projectId: string
    projectName: string
  }>
}

export interface UserDetail extends User {
  updatedAt: string
  projects: Array<{
    projectId: string
    projectName: string
    projectType: string
    roleId: string
    roleName: string
    joinedAt: string
  }>
}

export interface CreateUserInput {
  email: string
  name?: string
  roleId?: string
}

export interface UpdateUserInput {
  name?: string
  email?: string
  avatarUrl?: string | null
}

// ============================================
// API Response Types
// ============================================

interface PaginatedResponse<T> {
  success: boolean
  data: T[]
  meta: {
    pagination: {
      total: number
      page: number
      pageSize: number
      totalPages: number
    }
  }
}

interface ApiResponse<T> {
  success: boolean
  data: T
  message?: string
}

// ============================================
// Fetch helpers
// ============================================

async function fetchUsers(
  token: string | null,
  params?: { page?: number; pageSize?: number; search?: string }
): Promise<{ users: User[]; total: number }> {
  if (!token) return { users: [], total: 0 }

  const searchParams = new URLSearchParams()
  if (params?.page) searchParams.set("page", params.page.toString())
  if (params?.pageSize) searchParams.set("pageSize", params.pageSize.toString())
  if (params?.search) searchParams.set("search", params.search)

  const queryString = searchParams.toString()
  const response = await fetch(
    `${API_BASE}/api/users${queryString ? `?${queryString}` : ""}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  )

  if (!response.ok) {
    if (response.status === 401) return { users: [], total: 0 }
    throw new Error("Failed to fetch users")
  }

  const data: PaginatedResponse<User> = await response.json()
  return {
    users: data.data,
    total: data.meta.pagination.total,
  }
}

async function fetchUser(userId: string, token: string | null): Promise<UserDetail | null> {
  if (!token) return null

  const response = await fetch(`${API_BASE}/api/users/${userId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  })

  if (!response.ok) {
    if (response.status === 404) return null
    throw new Error("Failed to fetch user")
  }

  const data: ApiResponse<UserDetail> = await response.json()
  return data.data
}

async function createUser(input: CreateUserInput, token: string | null): Promise<User> {
  if (!token) throw new Error("Authentication required")

  const response = await fetch(`${API_BASE}/api/users`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(input),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }))
    throw new Error(error.message || "Failed to create user")
  }

  const data: ApiResponse<User> = await response.json()
  return data.data
}

async function updateUser(
  userId: string,
  input: UpdateUserInput,
  token: string | null
): Promise<User> {
  if (!token) throw new Error("Authentication required")

  const response = await fetch(`${API_BASE}/api/users/${userId}`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(input),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }))
    throw new Error(error.message || "Failed to update user")
  }

  const data: ApiResponse<User> = await response.json()
  return data.data
}

async function deleteUser(userId: string, token: string | null): Promise<void> {
  if (!token) throw new Error("Authentication required")

  const response = await fetch(`${API_BASE}/api/users/${userId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }))
    throw new Error(error.message || "Failed to delete user")
  }
}

// ============================================
// Hooks
// ============================================

export function useUsers(params?: { page?: number; pageSize?: number; search?: string }) {
  const token = useAuthToken()

  return useQuery({
    queryKey: ["users", params],
    queryFn: () => fetchUsers(token, params),
    enabled: !!token,
  })
}

export function useUser(userId: string) {
  const token = useAuthToken()

  return useQuery({
    queryKey: ["users", userId],
    queryFn: () => fetchUser(userId, token),
    enabled: !!userId && !!token,
  })
}

export function useCreateUser() {
  const queryClient = useQueryClient()
  const token = useAuthToken()

  return useMutation({
    mutationFn: (input: CreateUserInput) => createUser(input, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] })
    },
  })
}

export function useUpdateUser() {
  const queryClient = useQueryClient()
  const token = useAuthToken()

  return useMutation({
    mutationFn: ({ userId, ...input }: UpdateUserInput & { userId: string }) =>
      updateUser(userId, input, token),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["users"] })
      queryClient.invalidateQueries({ queryKey: ["users", variables.userId] })
    },
  })
}

export function useDeleteUser() {
  const queryClient = useQueryClient()
  const token = useAuthToken()

  return useMutation({
    mutationFn: (userId: string) => deleteUser(userId, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] })
    },
  })
}

export function useInviteUser() {
  const queryClient = useQueryClient()
  const token = useAuthToken()

  return useMutation({
    mutationFn: (input: { email: string; roleId?: string }) => createUser(input, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] })
    },
  })
}

// Search users for collaborator selection
export function useSearchUsers(search: string) {
  const token = useAuthToken();
  return useQuery({
    queryKey: ["users", "search", search],
    queryFn: () => fetchUsers(token, { search, pageSize: 10 }),
    enabled: !!token && search.length >= 2, // Only search when at least 2 characters
    staleTime: 30000, // Cache for 30 seconds
  })
}
